import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { channels, RealtimeEventName, type OrderSummary, type Page } from '@outfalak/shared'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { useRealtimeEvent } from '../hooks/useRealtime.js'
import { ago, dateTime, humanise, initials, money, timeOnly } from '../lib/format.js'
import {
  Badge,
  Card,
  Drawer,
  DrawerSection,
  Empty,
  Field,
  KeyValue,
  Notice,
  PageHead,
  Pager,
  RowsSkeleton,
  SearchInput,
  Stat,
  Tabs,
  Timeline,
} from '../components/ui.js'
import {
  BagIcon,
  CardIcon,
  DownloadIcon,
  ExternalIcon,
  EyeIcon,
  FilterIcon,
  MapPinIcon,
  PhoneIcon,
  ReceiptIcon,
  RupeeIcon,
  SparkIcon,
  StoreIcon,
  TruckIcon,
  UndoIcon,
} from '../components/icons.js'

const PER_PAGE = 25

export function OrdersPage(): React.ReactNode {
  const { shop } = useAuth()
  const queryClient = useQueryClient()
  /*
   * Seeded from `?q=`, so a link can land on one order.
   *
   * There is no `/orders/:id` route — the detail is a drawer over this table —
   * which leaves the search box as the only way for anything outside this
   * screen to point at a single order. The notifications menu does exactly
   * that. Read once as the initial value rather than watched: the merchant
   * typing over it must not be fighting the URL they arrived on.
   */
  const [params] = useSearchParams()
  const [search, setSearch] = useState(() => params.get('q') ?? '')
  const [status, setStatus] = useState('')
  const [method, setMethod] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<OrderSummary | null>(null)
  /** Orders that arrived over the socket since the last fetch, newest first. */
  const [fresh, setFresh] = useState<OrderSummary[]>([])

  const { data, isPending, error } = useQuery({
    queryKey: ['orders', shop?.id, search, status, method, page],
    queryFn: () =>
      api<Page<OrderSummary>>('/v1/orders', {
        query: { q: search, paymentStatus: status, paymentMethod: method, page, perPage: PER_PAGE },
      }),
    enabled: Boolean(shop?.id),
  })

  const ordersChannel = shop ? channels.shopOrders(shop.id) : null

  // Prepended to the list rather than triggering a refetch. A merchant watching
  // this screen during a sale wants the order to appear where they are looking,
  // not to have the table jump back to page one under their cursor.
  useRealtimeEvent(ordersChannel, RealtimeEventName.OrderCreated, (order) => {
    setFresh((current) => (current.some((row) => row.id === order.id) ? current : [order, ...current]))
  })

  useRealtimeEvent(ordersChannel, RealtimeEventName.OrderUpdated, () => {
    void queryClient.invalidateQueries({ queryKey: ['orders', shop?.id] })
  })

  const rows = useMemo(() => [...fresh, ...(data?.items ?? [])], [fresh, data])

  function resetTo(next: () => void): void {
    next()
    setPage(1)
    setFresh([])
  }

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Choose a store from the switcher above to see its orders.
        </Empty>
      </Card>
    )
  }

  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE))

  return (
    <>
      <PageHead
        title="Orders"
        subtitle="Manage and track every order from your stores in one place."
        actions={
          <>
            <button type="button" className="btn" onClick={() => window.print()}>
              <DownloadIcon size={16} />
              Export
            </button>
          </>
        }
      />

      <section className="grid grid--4">
        <Stat label="Total orders" value={total.toLocaleString('en-IN')} icon={<BagIcon size={20} />} tone="var(--brand)" />
        <Stat
          label="Paid"
          value={rows.filter((row) => row.paymentStatus === 'paid').length.toLocaleString('en-IN')}
          icon={<RupeeIcon size={20} />}
          tone="var(--ok)"
          footnote={<span className="faint">on this page</span>}
        />
        <Stat
          label="COD pending"
          value={rows.filter((row) => row.paymentStatus === 'cod_pending').length.toLocaleString('en-IN')}
          icon={<TruckIcon size={20} />}
          tone="var(--warn)"
          footnote={<span className="faint">on this page</span>}
        />
        <Stat
          label="Failed or refunded"
          value={rows.filter((row) => row.paymentStatus === 'failed' || row.paymentStatus === 'refunded').length.toLocaleString('en-IN')}
          icon={<UndoIcon size={20} />}
          tone="var(--bad)"
          footnote={<span className="faint">on this page</span>}
        />
      </section>

      {fresh.length > 0 && (
        <button
          type="button"
          className="notice notice--ok"
          style={{ width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
          onClick={() => {
            setFresh([])
            void queryClient.invalidateQueries({ queryKey: ['orders', shop.id] })
          }}
        >
          <SparkIcon size={17} />
          <span className="notice__body">
            <strong>
              {fresh.length} new order{fresh.length === 1 ? '' : 's'} arrived
            </strong>
            Click to fold them into the list.
          </span>
        </button>
      )}

      <Card flush>
        <Tabs
          value={status === '' ? 'all' : status}
          onChange={(key) => resetTo(() => setStatus(key === 'all' ? '' : key))}
          tabs={[
            { key: 'all', label: 'All orders' },
            { key: 'paid', label: 'Paid' },
            { key: 'cod_pending', label: 'COD pending' },
            { key: 'pending', label: 'Pending' },
            { key: 'failed', label: 'Failed' },
            { key: 'refunded', label: 'Refunded' },
          ]}
        />

        <div className="toolbar">
          <SearchInput value={search} onChange={(next) => resetTo(() => setSearch(next))} placeholder="Order number, phone or email" />

          <select className="select" value={method} onChange={(event) => resetTo(() => setMethod(event.target.value))} aria-label="Payment method">
            <option value="">All payment methods</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="netbanking">Netbanking</option>
            <option value="wallet">Wallet</option>
            <option value="cod">Cash on Delivery</option>
          </select>

          <button type="button" className="btn btn--ghost btn--sm" onClick={() => resetTo(() => { setSearch(''); setStatus(''); setMethod('') })}>
            <FilterIcon size={15} />
            Clear filters
          </button>
        </div>

        {error && (
          <Empty error title="Could not load orders">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && rows.length === 0 && <RowsSkeleton rows={8} />}

        {!isPending && rows.length === 0 && !error && (
          <Empty icon={<BagIcon size={24} />} title="No orders match those filters">
            Try clearing the search box or widening the status filter.
          </Empty>
        )}

        {rows.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Placed</th>
                    <th className="right">Amount</th>
                    <th className="right">Commission</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((order) => (
                    <tr
                      key={order.id}
                      className={[
                        'is-clickable',
                        fresh.some((row) => row.id === order.id) ? 'is-new' : '',
                        selected?.id === order.id ? 'is-selected' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setSelected(order)}
                    >
                      <td>
                        <span className="table__id">{order.orderNumber}</span>
                        <span className="table__sub">
                          {order.itemCount} item{order.itemCount === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td>
                        <span className="table__cell">
                          <span className="avatar avatar--sm avatar--muted">{initials(order.customerName ?? 'Guest')}</span>
                          <span>
                            {order.customerName ?? 'Guest'}
                            <span className="table__sub">{order.phone}</span>
                          </span>
                        </span>
                      </td>
                      <td>
                        {dateTime(order.createdAt).split(',')[0]}
                        <span className="table__sub">{timeOnly(order.createdAt)}</span>
                      </td>
                      <td className="right num">
                        {money(order.total, order.currency)}
                      </td>
                      <td className="right num">
                        {Number(order.commission ?? 0) > 0 ? money(order.commission, order.currency) : '—'}
                        {order.commissionPaidBy && Number(order.commission ?? 0) > 0 && (
                          <span className="table__sub">{order.commissionPaidBy === 'customer' ? 'Paid by customer' : 'Paid by you'}</span>
                        )}
                      </td>
                      <td>
                        {humanise(order.paymentMethod)}
                        <span className="table__sub">{order.paymentMethod === 'cod' ? 'COD' : 'Online'}</span>
                      </td>
                      <td>
                        <Badge tone={order.paymentStatus}>{humanise(order.paymentStatus)}</Badge>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`View ${order.orderNumber}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            setSelected(order)
                          }}
                        >
                          <EyeIcon size={17} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <Pager page={page} pageCount={pageCount} total={total} perPage={PER_PAGE} onPage={setPage} noun="orders" />
            )}
          </>
        )}
      </Card>

      <OrderDrawer order={selected} onClose={() => setSelected(null)} />
    </>
  )
}

// ── Detail drawer ───────────────────────────────────────────────────────────

/** What `/v1/orders/:orderNumber` returns, narrowed to what this panel reads. */
interface OrderDetail {
  orderNumber: string
  phone: string
  email: string | null
  currency: string
  subtotal: string
  shipping: string
  discount: string
  couponCode: string | null
  codFee: string
  total: string
  paymentMethod: string
  paymentStatus: string
  orderStatus: string
  gateway: string
  gatewayPaymentId: string | null
  paidAt: string | null
  createdAt: string
  shippingMethodLabel: string | null
  estimatedDelivery: string | null
  shippingAddress: {
    name?: string
    line1?: string
    landmark?: string
    city?: string
    state?: string
    pincode?: string
    country?: string
  } | null
  items: Array<{ title?: string; name?: string; quantity?: number; price?: string | number; image?: string }> | null
  customer: { name: string | null; email: string | null; phone: string | null; ordersCount: number | null } | null
  /** What is left to refund, decided by the API — see the detail route. */
  refundable: string
  refunds: Array<{ id: string; amount: string; status: string; createdAt: string }>
  eventLogs: Array<{ id: string; event: string; createdAt: string }>
}

/**
 * Fetched by order number, not rendered from the row.
 *
 * The list endpoint returns a summary — no line items, no address, no gateway
 * reference. Those are exactly what a merchant opens this panel for, so the
 * drawer asks for the full record. The summary it was opened from fills the
 * header immediately, so the panel is never blank while that request is in
 * flight.
 */
function OrderDrawer({ order, onClose }: { order: OrderSummary | null; onClose: () => void }): React.ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['order', order?.orderNumber],
    queryFn: () => api<OrderDetail>(`/v1/orders/${encodeURIComponent(order!.orderNumber)}`),
    enabled: Boolean(order?.orderNumber),
  })

  if (!order) return null

  const settled = order.paymentStatus === 'paid'
  const currency = data?.currency ?? order.currency
  const address = data?.shippingAddress
  const items = data?.items ?? []

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <span className="row">
          <strong style={{ fontSize: '1rem' }}>{order.orderNumber}</strong>
          <Badge tone={order.paymentStatus}>{humanise(order.paymentStatus)}</Badge>
        </span>
      }
      subtitle={`${dateTime(order.createdAt)} · ${ago(order.createdAt)}`}
      footer={
        <>
          <button type="button" className="btn btn--danger" disabled={!settled}>
            <UndoIcon size={16} />
            Refund
          </button>
          <button type="button" className="btn">
            <ReceiptIcon size={16} />
            Invoice
          </button>
          <button type="button" className="btn btn--primary">
            <ExternalIcon size={16} />
            View in store
          </button>
        </>
      }
    >
      <DrawerSection title="Customer">
        <div className="row">
          <span className="avatar">{initials(data?.customer?.name ?? order.customerName ?? 'Guest')}</span>
          <span className="list__text">
            <strong>{data?.customer?.name ?? order.customerName ?? 'Guest'}</strong>
            <small>
              <PhoneIcon size={12} style={{ verticalAlign: '-2px' }} /> {data?.phone ?? order.phone}
              {data?.customer?.ordersCount ? ` · ${data.customer.ordersCount} orders` : ''}
            </small>
          </span>
        </div>
        {(data?.email ?? data?.customer?.email) && (
          <p className="muted" style={{ fontSize: '.85rem' }}>
            {data?.email ?? data?.customer?.email}
          </p>
        )}
      </DrawerSection>

      {error && (
        <Empty error title="Could not load the full order">
          {(error as Error).message}
        </Empty>
      )}

      {isPending && !error && <RowsSkeleton rows={5} />}

      {items.length > 0 && (
        <DrawerSection title={`Items (${items.length})`}>
          <ul className="list list--plain">
            {items.map((item, index) => (
              <li key={`${item.title ?? item.name ?? 'item'}-${index}`} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <span className="list__text">
                  <strong>{item.title ?? item.name ?? 'Item'}</strong>
                  <small>Qty {item.quantity ?? 1}</small>
                </span>
                <span className="list__value">{money(item.price ?? null, currency)}</span>
              </li>
            ))}
          </ul>
        </DrawerSection>
      )}

      {data && (
        <DrawerSection title="Order summary">
          <KeyValue
            rows={[
              { label: 'Subtotal', value: money(data.subtotal, currency) },
              ...(Number(data.discount) > 0
                ? [{ label: data.couponCode ? `Discount (${data.couponCode})` : 'Discount', value: `− ${money(data.discount, currency)}`, kind: 'credit' as const }]
                : []),
              { label: data.shippingMethodLabel ?? 'Shipping', value: Number(data.shipping) > 0 ? money(data.shipping, currency) : 'Free' },
              ...(Number(data.codFee) > 0 ? [{ label: 'COD fee', value: money(data.codFee, currency) }] : []),
              { label: 'Total', value: money(data.total, currency), kind: 'total' as const },
            ]}
          />
        </DrawerSection>
      )}

      <DrawerSection title="Payment">
        <div className="row">
          <span className="stat__icon" style={{ width: 34, height: 34, marginBottom: 0, ['--tone' as string]: settled ? 'var(--ok)' : 'var(--warn)' }}>
            <CardIcon size={16} />
          </span>
          <span className="list__text">
            <strong>{humanise(data?.paymentMethod ?? order.paymentMethod)}</strong>
            <small>
              {(data?.paymentMethod ?? order.paymentMethod) === 'cod' ? 'Collected on delivery' : `via ${humanise(data?.gateway ?? 'gateway')}`}
            </small>
          </span>
          <Badge tone={order.paymentStatus}>{humanise(order.paymentStatus)}</Badge>
        </div>
        {data?.gatewayPaymentId && (
          <p className="muted" style={{ fontSize: '.8rem' }}>
            Payment ID: <code>{data.gatewayPaymentId}</code>
          </p>
        )}
      </DrawerSection>

      {address && (
        <DrawerSection title="Delivery address">
          <p className="row" style={{ alignItems: 'flex-start', fontSize: '.86rem' }}>
            <MapPinIcon size={15} style={{ color: 'var(--ink-faint)', marginTop: 2 }} />
            <span>
              {address.name && (
                <>
                  <strong>{address.name}</strong>
                  <br />
                </>
              )}
              {address.line1}
              {address.landmark ? `, ${address.landmark}` : ''}
              <br />
              {[address.city, address.state].filter(Boolean).join(', ')}
              {address.pincode ? ` — ${address.pincode}` : ''}
            </span>
          </p>
          {data?.estimatedDelivery && (
            <p className="muted" style={{ fontSize: '.82rem' }}>
              Estimated delivery {dateTime(data.estimatedDelivery).split(',')[0]}
            </p>
          )}
        </DrawerSection>
      )}

      {data && (data.refunds.length > 0 || Number(data.refundable) > 0) && (
        <DrawerSection title="Refunds">
          <ul className="list list--plain">
            {data.refunds.map((refund) => (
              <li key={refund.id} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <span className="list__text">
                  <strong>{money(refund.amount, currency)}</strong>
                  <small>{dateTime(refund.createdAt)}</small>
                </span>
                <Badge tone={refund.status}>{humanise(refund.status)}</Badge>
              </li>
            ))}
          </ul>

          {Number(data.refundable) > 0 && (
            <RefundBox orderNumber={order.orderNumber} refundable={data.refundable} currency={currency} />
          )}
        </DrawerSection>
      )}

      <DrawerSection title="Timeline">
        <Timeline
          events={[
            { label: 'Order placed', at: dateTime(data?.createdAt ?? order.createdAt), state: 'done' },
            {
              label: settled ? 'Payment successful' : 'Awaiting payment',
              at: data?.paidAt ? dateTime(data.paidAt) : null,
              state: settled ? 'done' : 'current',
            },
            {
              label: 'Order confirmed',
              at: data?.orderStatus === 'confirmed' ? dateTime(data.createdAt) : null,
              state: data?.orderStatus === 'confirmed' ? 'done' : settled ? 'current' : 'todo',
            },
            { label: 'Shipped', at: null, state: 'todo' },
            { label: 'Delivered', at: null, state: 'todo' },
          ]}
        />
      </DrawerSection>
    </Drawer>
  )
}

/**
 * Returning a shopper's money, from the order that took it.
 *
 * Rendered only where a refund is actually possible — a paid order with
 * something left on it — so the button never appears on an order it would
 * refuse. `refundable` comes from the API rather than being worked out here:
 * it is `total` minus every pending *and* processed refund, and a second copy
 * of that rule in the browser is one that disagrees with the server the first
 * time either changes.
 *
 * The amount defaults to the whole remaining balance, which is what a merchant
 * wants nine times out of ten, and is editable for the tenth.
 */
function RefundBox({ orderNumber, refundable, currency }: {
  orderNumber: string
  refundable: string
  currency: string
}): React.ReactNode {
  const client = useQueryClient()
  const [amount, setAmount] = useState(refundable)
  const [reason, setReason] = useState('')
  const [open, setOpen] = useState(false)

  const issue = useMutation({
    mutationFn: () =>
      api<{ reference: string; status: string; refundable: string }>(
        `/v1/orders/${encodeURIComponent(orderNumber)}/refund`,
        { method: 'POST', body: { amount: Number(amount), reason: reason.trim() || undefined } },
      ),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['order', orderNumber] })
      void client.invalidateQueries({ queryKey: ['orders'] })
      setOpen(false)
      setAmount(result.refundable)
      setReason('')
    },
  })

  if (!open) {
    return (
      <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
        Refund {money(refundable, currency)}
      </button>
    )
  }

  return (
    <div className="stack">
      <Field label="Amount" hint={`${money(refundable, currency)} available`}>
        <input
          type="number"
          className="input"
          value={amount}
          min="0.01"
          max={refundable}
          step="0.01"
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>

      <Field label="Reason" hint="Shown on your own records only.">
        <input
          type="text"
          className="input"
          value={reason}
          maxLength={255}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>

      {issue.error && (
        <Notice kind="error" title="The refund was not issued">
          {(issue.error as Error).message}
        </Notice>
      )}

      {/* Said plainly because it is the part merchants get wrong: every Indian
          gateway settles a refund over days, and an order that flips to
          "refunded" here has not yet put money in the shopper's account. */}
      <Notice kind="info" title="This takes a few days to reach the shopper">
        The gateway accepts the refund now and settles it to their account afterwards, usually within five to seven working
        days.
      </Notice>

      <div className="row">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={issue.isPending || Number(amount) <= 0 || Number(amount) > Number(refundable) + 0.01}
          onClick={() => issue.mutate()}
        >
          {issue.isPending ? 'Refunding…' : `Refund ${money(amount || '0', currency)}`}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={issue.isPending}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
